import { endOfDay, startOfDay } from "date-fns";
import React from "react";

import { CheckboxGroup, DateRangePicker, InputWrapper, RadioButtons } from "@performyard/py-components";

import FilterPanel from "../../../../components/Panel/FilterPanel";
import Spacer from "../../../../components/Spacer";

import { capitalizeWord } from "../../../../utils/stringUtils";

import { config } from "../config";
import type { WorkspaceDetailsFilters } from "../schema";

export const WorkspaceDetailsFilterPanel = ({
	isOpen,
	onClose,
	onApplyFilters,
	filters,
	updateForm,
	isAdminView = false,
}: {
	isOpen: boolean;
	onClose: () => void;
	onApplyFilters: (filters: WorkspaceDetailsFilters) => void;
	filters: WorkspaceDetailsFilters;
	updateForm: (newFilters: Partial<WorkspaceDetailsFilters>) => void;
	isAdminView?: boolean;
}) => {
	const statusOptions = config.statusOptions.map((status) => ({
		value: status,
		label: capitalizeWord(status),
	}));

	const privateNotesRadioOptions = config.privateNotesOptions.map((option) => ({
		value: option,
		label: option === "any" ? "Any" : option === "has-private-notes" ? "Has Private Notes" : "No Private Notes",
	}));

	return (
		<FilterPanel
			isOpen={isOpen}
			onClose={onClose}
			title={"Filters"}
			onSaveFilters={() => {
				onApplyFilters(filters);
				onClose();
			}}
		>
			<InputWrapper id="status-filter" label="Status">
				<CheckboxGroup
					id="status-filter"
					options={statusOptions}
					value={filters?.status || []}
					onChange={(selectedStatuses) => {
						updateForm({ status: selectedStatuses });
					}}
				/>
			</InputWrapper>
			<Spacer height={2} />
			<InputWrapper label="Meeting Date" id="meeting-date-filter">
				<DateRangePicker
					id="meeting-date-filter"
					value={{
						from: filters.meetingDateStart,
						to: filters.meetingDateEnd,
					}}
					onChange={(date) => {
						// Always set start date to beginning of day and end date to end of day
						// to ensure all datetimes within the range are captured
						const fromDate = date?.from ? startOfDay(date.from) : undefined;
						const toDate = date?.to ? endOfDay(date.to) : undefined;

						updateForm({
							meetingDateStart: fromDate,
							meetingDateEnd: toDate,
						});
					}}
					popoverContentClassName="z-[1100]"
					menuAlign="start"
					hasClearButton
				/>
			</InputWrapper>
			{!isAdminView && (
				<>
					<Spacer height={2} />
					<InputWrapper id="private-notes-filter" label="Private Notes">
						<RadioButtons
							id="private-notes-filter"
							options={privateNotesRadioOptions}
							value={filters?.privateNotes || "any"}
							onChange={(selectedOption) => {
								updateForm({ privateNotes: selectedOption });
							}}
						/>
					</InputWrapper>
				</>
			)}
		</FilterPanel>
	);
};

export default WorkspaceDetailsFilterPanel;
